import { useState } from 'react';
import Card from '../../../components/Card.jsx';
import Button from '../../../components/Button.jsx';
import Chip from '../../../components/Chip.jsx';
import DataTable from '../../../components/DataTable.jsx';
import { backtestApi } from '../../../lib/apiClient.js';
import ConfigPanel from '../ConfigPanel.jsx';
import StrategyPreview from '../StrategyPreview.jsx';
import ErrorNote from '../ErrorNote.jsx';
import EquityCurveChart from '../EquityCurveChart.jsx';
import useRunResult from '../../../utils/useRunResult.js';
import { engineConfig } from '../../../utils/engineConfig.js';
import { number, percent } from '../../../utils/format.js';
import { DEFAULT_OPTIMIZE_PARAMETER } from '../../../utils/backtestStudio.js';
import { isBacktestPeriodValid } from '../../../utils/backtestPeriod.js';

const FOLD_COLUMNS = [
  { key: 'fold', header: 'Fold' },
  { key: 'isPeriod', header: 'In-sample' },
  { key: 'isSharpe', header: 'IS Sharpe', numeric: true, render: (r) => number(r.isMetrics?.sharpe) },
  { key: 'oosPeriod', header: 'Out-of-sample' },
  { key: 'oosReturn', header: 'OOS return', numeric: true, render: (r) => percent(r.oosMetrics?.totalReturn) },
  { key: 'oosSharpe', header: 'OOS Sharpe', numeric: true, render: (r) => number(r.oosMetrics?.sharpe) },
];

/**
 * Walk-forward mode: base BacktestRequest + grid + folds → per-fold IS vs
 * OOS metrics + a stitched OOS equity curve, per OBT_INTEGRATION_SPEC.md
 * §1 (MLWalkforwardRequest = "rolling retrain → stitched OOS equity").
 */
export default function WalkForwardMode({ config, onConfigChange, wallet, onRan, lastRun, onSaveRun, rulesStrategy }) {
  const [paramName, setParamName] = useState(DEFAULT_OPTIMIZE_PARAMETER);
  const [gridMin, setGridMin] = useState(10);
  const [gridMax, setGridMax] = useState(40);
  const [folds, setFolds] = useState(4);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useRunResult(lastRun?.result, true);
  const [error, setError] = useState(null);

  const request = {
    base: {
      mode: 'rules',
      strategy: rulesStrategy,
      symbols: config.symbols,
      period: config.period,
      config: engineConfig(config),
    },
    grid: { [paramName]: [gridMin, Math.round((gridMin + gridMax) / 2), gridMax] },
    folds,
  };

  async function run() {
    setError(null);
    setRunning(true);
    try {
      const data = await backtestApi.walkforward(request);
      setResult(data);
      onSaveRun?.(data);
      onRan?.();
    } catch (err) {
      setError(err);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="aq-grid aq-grid--2">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ConfigPanel config={config} onChange={onConfigChange} wallet={wallet} />

        <Card>
          <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
            Walk-forward setup
          </h3>
          <div className="aq-bt-params">
            <label className="aq-field">
              <span>Param name</span>
              <input type="text" value={paramName} onChange={(e) => setParamName(e.target.value)} />
            </label>
            <label className="aq-field">
              <span>Grid min</span>
              <input type="number" value={gridMin} onChange={(e) => setGridMin(Number(e.target.value))} />
            </label>
            <label className="aq-field">
              <span>Grid max</span>
              <input type="number" value={gridMax} onChange={(e) => setGridMax(Number(e.target.value))} />
            </label>
            <label className="aq-field">
              <span>Folds</span>
              <input type="number" value={folds} onChange={(e) => setFolds(Number(e.target.value))} />
            </label>
          </div>

          <StrategyPreview title="walkforward request preview" value={request} />

          <div style={{ marginTop: 14 }}>
            <Button disabled={running || !rulesStrategy || !isBacktestPeriodValid(config.period)} onClick={run}>
              {running ? 'Running…' : 'Run walk-forward'}
            </Button>
            {!rulesStrategy && <p className="aq-note">Build a strategy in Rules Builder before running walk-forward validation.</p>}
          </div>
        </Card>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card>
          <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
            Stitched OOS equity
          </h3>
          <ErrorNote error={error} />
          {!result && !error && <p className="aq-module-desc">Run walk-forward to see stitched out-of-sample equity.</p>}
          {result?.stitchedEquity?.length ? <EquityCurveChart equity={result.stitchedEquity} label="Stitched OOS" /> : result && <p className="aq-module-desc">{result.aggregate?.verdict || 'Walk-forward completed.'}</p>}
        </Card>

        {result && (
          <Card>
            <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
              IS vs OOS by fold
            </h3>
            <DataTable columns={result.fold_results ? [
              { key: 'fold', header: 'Fold' },
              { key: 'train', header: 'In-sample', render: (r) => (r.train || []).join(' → ') },
              { key: 'test', header: 'Out-of-sample', render: (r) => (r.test || []).join(' → ') },
              { key: 'is_sharpe', header: 'IS Sharpe', numeric: true },
              { key: 'oos_sharpe', header: 'OOS Sharpe', numeric: true },
              { key: 'oos_return', header: 'OOS return', numeric: true, render: (r) => r.oos_return == null ? '—' : `${Number(r.oos_return).toFixed(2)}%` },
            ] : FOLD_COLUMNS} rows={(result.fold_results || result.folds || []).map((f, index) => ({ ...f, id: f.fold || index + 1, fold: f.fold || index + 1 }))} />
            <div className="aq-metric-row" style={{ marginTop: 10 }}>
              <span>Average Sharpe degradation (OOS − IS)</span>
              {result.degradation ? <Chip tone={result.degradation.sharpe < 0 ? 'loss' : 'gain'}>
                {result.degradation.sharpe >= 0 ? '+' : ''}{result.degradation.sharpe} ({result.degradation.sharpePct}%)
              </Chip> : <Chip tone="info">{result.aggregate?.verdict || 'Complete'}</Chip>}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
